import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ChevronLeft, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const genSession = () => {
  const existing = localStorage.getItem("society_chat_session");
  if (existing) return existing;
  const id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem("society_chat_session", id);
  return id;
};

const INITIAL_GREETING = {
  role: "assistant",
  content:
    "Welcome to Society. I'm Velvet — I can share our menu, prices, hours, and help you leave a message for the team. What brings you in today?",
};

const SUGGESTIONS = [
  "What are your hours?",
  "How much is a facial?",
  "Where are you located?",
  "Tell me about lash extensions",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // 'chat' | 'leave'
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [leaveStatus, setLeaveStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [unread, setUnread] = useState(false);

  const [sessionId] = useState(genSession);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, view]);

  // Focus input when chat opens
  useEffect(() => {
    if (open && view === "chat") {
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [open, view]);

  // After 8s on first load, gently pulse to invite
  useEffect(() => {
    const t = setTimeout(() => setUnread(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;

    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });

      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          // Strip exactly "data: " prefix without trimming token whitespace
          const raw = line.startsWith("data: ") ? line.slice(6) : line.slice(5);
          if (!raw || raw === "[DONE]") continue;
          // Restore escaped newlines
          const piece = raw.replace(/\\n/g, "\n");
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = {
              role: "assistant",
              content: copy[copy.length - 1].content + piece,
            };
            return copy;
          });
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content:
            "Something glitched on my end. You can try again, or tap 'Leave a Message' and our team will follow up directly.",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    if (!leaveForm.name || !leaveForm.email || !leaveForm.message) return;
    setLeaveStatus("sending");
    try {
      const res = await fetch(`${API}/api/chat/leave-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...leaveForm, session_id: sessionId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLeaveStatus("sent");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Thanks, ${leaveForm.name.split(" ")[0]}. Your message is with the Society team — we'll be in touch within one business day.`,
        },
      ]);
      setLeaveForm({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => {
        setView("chat");
        setLeaveStatus(null);
      }, 2200);
    } catch {
      setLeaveStatus("error");
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => {
          setOpen((o) => !o);
          setUnread(false);
        }}
        data-testid="chatbot-toggle"
        aria-label={open ? "Close chat" : "Open chat"}
        className={`fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
          open
            ? "bg-white/10 backdrop-blur-md border border-white/15 rotate-90"
            : "bg-[var(--accent)] text-[#0a0a0a] hover:scale-105"
        }`}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unread && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[var(--warm-gold)] animate-ping" />
        )}
        {!open && unread && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[var(--warm-gold)]" />
        )}
      </button>

      {/* Panel */}
      <div
        data-testid="chatbot-panel"
        className={`fixed bottom-[88px] right-5 sm:bottom-24 sm:right-6 z-[59] w-[calc(100vw-2.5rem)] sm:w-[400px] max-w-[400px] h-[560px] max-h-[72vh] rounded-2xl border border-white/8 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] bg-[#0f0f12] backdrop-blur-xl overflow-hidden flex flex-col transition-all duration-300 origin-bottom-right ${
          open ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 pointer-events-none translate-y-2"
        }`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between bg-gradient-to-b from-white/[0.04] to-transparent">
          <div className="flex items-center gap-3">
            {view === "leave" && (
              <button
                onClick={() => setView("chat")}
                data-testid="chatbot-back"
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Back"
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <div className="relative">
              <div className="w-9 h-9 rounded-full bg-[var(--accent)]/15 border border-[var(--accent)]/40 flex items-center justify-center font-display text-[var(--accent)] text-base">
                V
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0f0f12]" />
            </div>
            <div>
              <p className="text-white text-sm font-medium leading-tight">Velvet</p>
              <p className="text-[10px] tracking-[0.22em] uppercase text-white/45">Society Concierge</p>
            </div>
          </div>
        </div>

        {/* Body */}
        {view === "chat" ? (
          <>
            <div
              ref={scrollRef}
              data-testid="chatbot-messages"
              className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[var(--accent)] text-[#0a0a0a] rounded-br-sm"
                        : "bg-white/[0.06] text-white/90 rounded-bl-sm border border-white/5"
                    }`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {messages.length === 1 && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      data-testid={`chat-suggestion-${s.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input + Email us CTA */}
            <div className="border-t border-white/8 p-3 bg-[#0c0c0e]">
              <button
                onClick={() => setView("leave")}
                data-testid="chatbot-leave-message"
                className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[var(--accent)]/50 bg-[var(--accent)]/10 text-[var(--accent)] text-xs font-medium tracking-[0.12em] uppercase hover:bg-[var(--accent)]/20 hover:border-[var(--accent)] transition-colors"
              >
                <Mail size={14} />
                Email Us Directly
              </button>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
                className="flex items-center gap-2"
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything…"
                  data-testid="chatbot-input"
                  disabled={sending}
                  className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 text-base sm:text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--accent)]/60 transition-colors disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  data-testid="chatbot-send"
                  className="w-10 h-10 rounded-full bg-[var(--accent)] text-[#0a0a0a] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 transition-transform"
                  aria-label="Send"
                >
                  <Send size={15} />
                </button>
              </form>
            </div>
          </>
        ) : (
          /* Leave Message form */
          <form
            onSubmit={submitLeave}
            data-testid="chatbot-leave-form"
            className="flex-1 overflow-y-auto p-5 space-y-4"
          >
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--accent)] mb-1">Email Us</p>
              <h3 className="font-display text-white text-2xl leading-tight">Send us a message.</h3>
              <p className="text-white/55 text-xs mt-2">Goes straight to info@welcometosociety.com — we&apos;ll reply within one business day.</p>
            </div>

            <div className="space-y-3">
              <input
                required
                placeholder="Your name"
                value={leaveForm.name}
                onChange={(e) => setLeaveForm({ ...leaveForm, name: e.target.value })}
                data-testid="leave-input-name"
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--accent)]/60"
              />
              <input
                required
                type="email"
                placeholder="Email"
                value={leaveForm.email}
                onChange={(e) => setLeaveForm({ ...leaveForm, email: e.target.value })}
                data-testid="leave-input-email"
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--accent)]/60"
              />
              <input
                placeholder="Phone (optional)"
                value={leaveForm.phone}
                onChange={(e) => setLeaveForm({ ...leaveForm, phone: e.target.value })}
                data-testid="leave-input-phone"
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--accent)]/60"
              />
              <textarea
                required
                rows={4}
                placeholder="What is on your mind?"
                value={leaveForm.message}
                onChange={(e) => setLeaveForm({ ...leaveForm, message: e.target.value })}
                data-testid="leave-input-message"
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--accent)]/60 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={leaveStatus === "sending" || leaveStatus === "sent"}
              data-testid="leave-submit"
              className="w-full bg-[var(--accent)] text-[#0a0a0a] rounded-full py-3 text-sm font-medium tracking-wide hover:scale-[1.01] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {leaveStatus === "sending"
                ? "Sending…"
                : leaveStatus === "sent"
                ? "Sent — thank you"
                : "Send Message"}
            </button>

            {leaveStatus === "error" && (
              <p className="text-xs text-red-400" data-testid="leave-error">
                Couldn&apos;t send right now. Please try again or email info@welcometosociety.com directly.
              </p>
            )}
          </form>
        )}
      </div>
    </>
  );
}

